import { LeftBar } from '../components/LeftBar.tsx';
import {
  ActionIcon,
  AppShell,
  Burger,
  Flex,
  Group,
  Select,
  Title,
  useMantineColorScheme,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import ProtectedRoute from './ProtectedRoute.tsx';
import { Outlet, useLocation } from 'react-router-dom';
import { Moon, Sun } from 'lucide-react';
import WelcomeModal from '../components/Welcome.modal.tsx';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppHeaderUserMenu } from '../components/AppHeaderUserMenu.tsx';
import { VrSessionChannelStatus } from '../components/VrSessionChannelStatus.tsx';
import DebugPanel from '../components/AblyDebugPanel.tsx';

const LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'it', label: 'Italiano' },
  { value: 'pt', label: 'Português' },
];

const NAVBAR_WIDTH_COLLAPSED = 80;
const NAVBAR_WIDTH_EXPANDED = 220;

const Main = () => {
  const { colorScheme, setColorScheme } = useMantineColorScheme({
    keepTransitions: true,
  });
  const { t, i18n } = useTranslation();
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] =
    useDisclosure();
  const [desktopNavbarHovered, setDesktopNavbarHovered] = useState(false);
  const location = useLocation();

  const changeLanguage = (lng: string | null) => {
    if (!lng) return;
    i18n.changeLanguage(lng).catch(() => {
      // Error changing language
    });
  };

  useEffect(() => {
    closeMobile();
  }, [location, closeMobile]);

  return (
    <ProtectedRoute>
      <AppShell
        layout={'alt'}
        header={{ height: 72 }}
        navbar={{
          width: desktopNavbarHovered
            ? NAVBAR_WIDTH_EXPANDED
            : NAVBAR_WIDTH_COLLAPSED,
          breakpoint: 'sm',
          collapsed: { mobile: !mobileOpened },
        }}
        padding='md'
      >
        <AppShell.Header p={'sm'}>
          <Flex
            align={'center'}
            style={{ height: '100%' }}
            justify={'space-between'}
            gap='md'
          >
            <Group gap='sm' wrap='nowrap'>
              <Burger
                opened={mobileOpened}
                onClick={toggleMobile}
                hiddenFrom='sm'
                size='sm'
              />
              <Title order={4}>BECOME.</Title>
            </Group>

            <Group gap='sm' wrap='nowrap'>
              <VrSessionChannelStatus />
              <ActionIcon
                variant={'subtle'}
                onClick={() =>
                  setColorScheme(colorScheme === 'light' ? 'dark' : 'light')
                }
              >
                {colorScheme === 'light' ? (
                  <Moon size={16} />
                ) : (
                  <Sun size={16} />
                )}
              </ActionIcon>
              <Select
                aria-label={t('header_language', 'Lingua')}
                data={LANGUAGE_OPTIONS}
                value={i18n.language}
                onChange={changeLanguage}
                allowDeselect={false}
                w={{ base: 120, sm: 140 }}
                comboboxProps={{ withinPortal: true }}
              />
              <AppHeaderUserMenu />
            </Group>
          </Flex>
        </AppShell.Header>

        <AppShell.Navbar>
          <LeftBar
            toggle={toggleMobile}
            desktopExpanded={desktopNavbarHovered}
            onDesktopHoverChange={setDesktopNavbarHovered}
          />
        </AppShell.Navbar>

        <AppShell.Main>
          <Outlet />
        </AppShell.Main>
      </AppShell>

      <WelcomeModal />
      <DebugPanel />
    </ProtectedRoute>
  );
};

export default Main;
